import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuthUser } from "@/lib/auth-context";

export default function Register(): JSX.Element {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const [email, setEmail] = useState<string | null>(null);
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [previewing, setPreviewing] = useState(true);
  const navigate = useNavigate();
  const { reload } = useAuthUser();

  useEffect(() => {
    if (!token) { setEmail(null); setPreviewing(false); return; }
    fetch(`/api/auth/invitation-preview?token=${encodeURIComponent(token)}`)
      .then(async (res) => {
        if (res.ok) {
          const data = (await res.json()) as { email: string };
          setEmail(data.email);
        } else {
          setEmail(null);
        }
      })
      .catch(() => setEmail(null))
      .finally(() => setPreviewing(false));
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const res = await fetch("/api/auth/register", {
        method: "POST",
        headers: { "content-type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ token, password }),
      });
      if (res.ok) {
        await reload();
        navigate("/onboarding", { replace: true });
      } else {
        const data = (await res.json()) as { error?: { message?: string } };
        setError(data.error?.message ?? "Registration failed.");
      }
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (previewing) return <main className="min-h-screen grid place-items-center p-8"><p className="text-muted-foreground">Loading…</p></main>;
  if (!token || email === null) {
    return (
      <main className="min-h-screen grid place-items-center p-8">
        <div className="max-w-sm w-full text-center">
          <h1 className="font-display text-2xl font-extrabold mb-2">Invalid invitation</h1>
          <p className="text-muted-foreground">This invitation link is invalid or has expired.</p>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen grid place-items-center p-8">
      <div className="max-w-sm w-full">
        <h1 className="font-display text-2xl font-extrabold mb-2 text-center">Create your account</h1>
        <p className="text-center text-sm text-muted-foreground mb-6">
          Invited as <span className="font-medium text-foreground">{email}</span>
        </p>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <label htmlFor="password" className="text-sm font-medium">Choose a password</label>
            <input
              id="password"
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
              className="rounded-control border border-border bg-card px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <p className="text-xs text-muted-foreground">Minimum 8 characters</p>
          </div>
          {error && <p role="alert" className="text-sm font-medium text-destructive">{error}</p>}
          <button
            type="submit"
            disabled={loading}
            className="rounded-control bg-primary px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
          >
            {loading ? "Creating account…" : "Create account"}
          </button>
        </form>
      </div>
    </main>
  );
}
